import {
  type MutableRefObject,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import Modal from '@components/Modal';
import { useModal } from '@components/Modal/ModalProvider';
import { useAnalysisPage } from '@features/analysis/useAnalysisPage';
import EditModelContent from './EditModelContent';
import { usePanelContainer } from '@features/analysis/rendering/Panels/usePanelContainer';
import { configStore } from 'config/configStore';
import { EditNames, EditTarget } from 'config/analysisConfig';
import useAuth from '@hooks/Auth/useAuth';
import { useBrowserPage } from '@features/browser/useBrowserPage';
import {
  loadRegulatorBuffer,
  update2DLabel,
  useBufferPool,
} from '@features/analysis/rendering/Panels/Renderers/vtkManagers';
import type { ContextPool, SurfaceContext, VRContext } from '@features/analysis/rendering/Panels/Renderers/vtkManagers';
import { LAYOUT_CONFIG } from 'config/layoutConfig';
import { getEditModeReturnState, saveEditModeReturnState } from './editModeReturnState';
import useModalActions from '@hooks/common/useModalAction';
import { initCameraForAnalysis as initCameraVR } from '@features/view/VRUtilities';
import { initCameraForAnalysis as initCameraSurface } from '@features/view/Surface3DUtilities';
import { clearSegmentManager, getTileMap, useSegmentManager } from '@features/analysis/segmentManager';
import { EDIT_WARNING_MESSAGE } from '@features/view/Modification';
import {
  clearConnect2DSelection,
  clearRegulatorSegmentation,
  clearSegmentation,
} from '@features/analysis/rendering/Panels/Renderers/CornerstoneManager';

interface props {
  updateActivatedTool: (tool: any, check: boolean) => void;
}

const EDIT_TOOL = {
  id: 'Edit',
  groupName: 'manual',
  isToggleable: true,
};

const EDIT_LUMEN_LAYOUT_TYPE = 'ffr-1-2-2';
const EDIT_LUMEN_VIEW_READY_TIMEOUT_MS = 30000;
const EDIT_LUMEN_VIEW_READY_POLL_MS = 100;

function hasConnectedCanvas(context: any) {
  const canvas = context?.canvas as HTMLCanvasElement | undefined;
  if (!canvas || !canvas.isConnected) return false;
  const { width, height } = canvas.getBoundingClientRect();
  return width > 0 && height > 0;
}

function areEditViewsReady(target: EditTarget, contextPool: ContextPool) {
  const cprContext = contextPool['CPR.0.straightened'] as any;
  const surfaceContext = contextPool.Surface3D as SurfaceContext;
  const actors = surfaceContext.renderer.getActors();
  const cprReady =
    target === 'lumen'
      ? hasConnectedCanvas(cprContext) &&
        !!cprContext?.actorIds?.CPRActor &&
        Array.isArray(cprContext?.path) &&
        cprContext.path.length > 0
      : true;

  const init = getTileMap().size > 0 && getTileMap().size <= actors.length;
  const surfaceReady = hasConnectedCanvas(surfaceContext) && init;
  return cprReady && surfaceReady;
}


function waitForEditViewsReady(
  target:EditTarget,
  contextPoolRef: MutableRefObject<ContextPool>,
  timeoutMs = EDIT_LUMEN_VIEW_READY_TIMEOUT_MS,
) {
  return new Promise<void>((resolve) => {
    const startedAt = performance.now();
    let timer: ReturnType<typeof window.setTimeout> | null = null;
    let done = false;

    const finish = () => {
      if (done) return;
      done = true;
      if (timer !== null) {
        window.clearTimeout(timer);
      }
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    };

    const check = () => {
      if (areEditViewsReady(target,contextPoolRef.current)) {
        finish();
        return;
      }

      if (performance.now() - startedAt >= timeoutMs) {
        finish();
        return;
      }

      timer = window.setTimeout(check, EDIT_LUMEN_VIEW_READY_POLL_MS);
    };

    check();
  });
}

const PLAQUE_EDIT_MODAL_CONFIG = {
  title: 'Edit Plaque',

  editQuestion: 'What would you like to edit?',

  warningMessage:
    'Changing the centerline will recalculate all plaque, stenosis, and lumen measurements for this vessel. Previous results will be overwritten.',

  editOptions: [
    {
      value: EditNames.centerline,
      title: 'Edit Centerline',
      description:
        'Modify the vessel path. This will recalculate plaque, stenosis, and lumen measurements.',
    },
    {
      value: EditNames.contour,
      title: 'Edit Contour',
      description:
        'Modify lumen/wall/plaque boundaries on selected cross-sections.',
    },
  ],
};

const FFR_EDIT_MODAL_CONFIG = {
  title: 'Edit Coronary Vessel',
  editQuestion: 'What would you like to edit?',
  warningMessage:
    'Changing the coronary will overwrite previous coronary results. Edit is not supported in Low quality mode.',
  editOptions: [
    {
      value: EditNames.regulator,
      title: 'Edit Regulator',
      description: 'Modify the vessel path to generate the coronary centerline.',
    },
    {
      value: EditNames.lumen,
      title: 'Expand Lumen',
      description: 'Modify the lumen boundaries to increase the vessel lumen diameter.',
    },
  ],
};

export function useEditModel({ updateActivatedTool }: props) {
  const { openModal, closeModal } = useModal();
  const { selectAction, waitingAction, closeLoadingModal } = useModalActions();
  const modalIdRef = useRef<string | null>(null);
  const appliedRef = useRef(false);
  const segmentManagerActiveRef = useRef(false);
  const editWaitingSeqRef = useRef(0);
  const { canApplyModification } = useSegmentManager();

  const {
    updateCurrentLayout,
    selectedAnalysis,
    selectedLayoutConfig,
    globalToolState,
    handleUpdateGlobalTool,
  } = useAnalysisPage();

  const { analysisType, updateSelectedPanelConfig } = usePanelContainer();
  const { accessToken } = useAuth();
  const { selectedSeries, selectedSeriesId } = useBrowserPage();
  const { bufferPool, contextPool } = useBufferPool();
  const contextPoolRef = useRef(contextPool);
  contextPoolRef.current = contextPool;
  const pool = selectedSeriesId ? bufferPool[selectedSeriesId] : undefined;
  const hasVR = selectedLayoutConfig.panelConfig.some(
    (panel) => panel.componentKey === 'VR',
  );


  const modalConfig =
    analysisType === 'plaque'
      ? PLAQUE_EDIT_MODAL_CONFIG
      : FFR_EDIT_MODAL_CONFIG;

  useEffect(() => {
    segmentManagerActiveRef.current =
      !!globalToolState?.toggles.ToggleSegmentationManager;
  }, [globalToolState?.toggles.ToggleSegmentationManager]);


  const closeWithFlagOff = useCallback(() => {
    const id = modalIdRef.current;
    if (!id) return;
    appliedRef.current = false;
    modalIdRef.current = null;
    updateActivatedTool(EDIT_TOOL, false);
    closeModal(id);
  }, [closeModal, updateActivatedTool]);

  const applyEditTarget = useCallback(
    async (target: EditTarget) => {
      if (configStore.get().EditMode === target) {
        appliedRef.current = true;
        closeWithFlagOff();
        return;
      }

      const isFfrEditTarget =
        target === EditNames.regulator || target === EditNames.lumen;
      if (isFfrEditTarget && analysisType !== 'FFR') {
        closeWithFlagOff();
        return;
      }
      if (!pool || !selectedSeriesId || !selectedLayoutConfig.type) {
        closeWithFlagOff();
        return;
      }

      const currentEditMode = configStore.get().EditMode;
      if (!currentEditMode || !getEditModeReturnState()) {
        saveEditModeReturnState({
          seriesId: selectedSeriesId,
          analysisType: selectedAnalysis?.analysisType ?? analysisType,
          layoutType: selectedLayoutConfig.type,
          segmentManagerWasActive:
            !!globalToolState?.toggles.ToggleSegmentationManager,
        });
      }

      const values = Object.values(configStore.get().label) as boolean[];
      const isSegmentedRegionSelected =
        values.length > 0 && values.every(Boolean);
      if (isFfrEditTarget && !isSegmentedRegionSelected) {
        update2DLabel();
      }

      appliedRef.current = true;

      clearConnect2DSelection();
      clearSegmentManager();
      clearSegmentation(selectedSeriesId, 'manual');
      clearRegulatorSegmentation(selectedSeriesId);
      let nextLayoutType: string | null = null;
      if (target === EditNames.regulator) {
        await loadRegulatorBuffer({
          pool,
          accessToken,
          selectedSeriesId,
        });
        nextLayoutType = '2-2';
      } else if (target === EditNames.lumen) {
        nextLayoutType = EDIT_LUMEN_LAYOUT_TYPE;
      }

      configStore.setData({
        EditMode: target,
        cprModificationTool:
          target === EditNames.lumen
            ? 'none'
            : configStore.get().cprModificationTool,

        manual3DTool: {
          eraser: false,
          initialPoint: false,
        },
      });

    const requiredLayout =
      target === EditNames.lumen
        ? EDIT_LUMEN_LAYOUT_TYPE
        : target === EditNames.regulator
          ? '2-2'
          : undefined;

      const shouldWaitForEditViews =
        requiredLayout != undefined &&
        nextLayoutType === requiredLayout &&
        selectedLayoutConfig.type !== requiredLayout;

      if (shouldWaitForEditViews) {
        waitingAction(
          { message: 'Waiting' },
          { timeoutMs: EDIT_LUMEN_VIEW_READY_TIMEOUT_MS + 1000 },
        );
      }

      if (nextLayoutType) {
        updateCurrentLayout(nextLayoutType);
        const nextLayoutConfig =
          LAYOUT_CONFIG.FFR.find((layout) => layout.type === nextLayoutType) ??
          selectedLayoutConfig;
        const defaultPanelConfig =
          nextLayoutConfig.panelConfig.find(
            (p) => p.panelId === 'DICOMPanel',
          ) ?? nextLayoutConfig.panelConfig[0];
        if (defaultPanelConfig) {
          updateSelectedPanelConfig(defaultPanelConfig);
        }
      }
      console.log({segmentManagerActiveRef:segmentManagerActiveRef.current})

      const isSegmentManagerActive =
        !!globalToolState?.toggles.ToggleSegmentationManager;
      if (!isSegmentManagerActive) {
        handleUpdateGlobalTool('ToggleSegmentationManager');
        segmentManagerActiveRef.current = true;
      }

      closeWithFlagOff();

      if (shouldWaitForEditViews) {
        const seq = editWaitingSeqRef.current + 1;
        editWaitingSeqRef.current = seq;
        waitForEditViewsReady(target,contextPoolRef)
          .catch((error) => {
            console.warn('[EditModel] edit lumen view waiting failed', error);
          })
          .finally(() => {
            if (editWaitingSeqRef.current === seq) {
              closeLoadingModal();
            }
          });
      }

      if (hasVR) {
        const context = {
          current: contextPoolRef.current['VR'] as VRContext | null,
        };
        initCameraVR(context, analysisType, selectedSeriesId!, pool);
      } else {
        const context = {
          current: contextPoolRef.current['Surface3D'] as SurfaceContext | null,
        };
        initCameraSurface(context, analysisType, selectedSeriesId!, pool!);
      }
    },
    [
      accessToken,
      analysisType,
      closeLoadingModal,
      closeWithFlagOff,
      globalToolState?.toggles.ToggleSegmentationManager,
      handleUpdateGlobalTool,
      pool,
      selectedAnalysis?.analysisType,
      selectedLayoutConfig,
      selectedSeries,
      selectedSeriesId,
      updateCurrentLayout,
      updateSelectedPanelConfig,
      waitingAction,
    ],
  );

  const handleApply = useCallback(
    async (target: EditTarget) => {
      if (!target || !selectedSeriesId || !selectedSeries) {
        closeWithFlagOff();
        return;
      }

      if (!canApplyModification) {
        await applyEditTarget(target);
        return;
      }

      selectAction(
        EDIT_WARNING_MESSAGE,
        async () => {
          await applyEditTarget(target);
        },
        { right: 'Confirm' },
        'compact-warning-modal',
      );
    },
    [
      applyEditTarget,
      canApplyModification,
      closeWithFlagOff,
      selectedSeries,
      selectedSeriesId,
      selectAction,
    ],
  );

  const openEditModel = useCallback(() => {
    if (modalIdRef.current) {
      return;
    }
    const isFfrLowQualityEditDisabled =
      analysisType === 'FFR' &&
      configStore.get().generalSetting.renderingQuality !== 'high';
    modalIdRef.current = openModal(
      <Modal
        type="page"
        close={closeWithFlagOff}
        description={
          <EditModelContent
            close={closeWithFlagOff}
            onApply={handleApply}
            isApplyDisabled={isFfrLowQualityEditDisabled}
            selectedTargetDefault={configStore.get().EditMode}
            {...modalConfig}
          />
        }
      />,
      false,
      closeWithFlagOff,
    );
    updateActivatedTool(EDIT_TOOL, true);
  }, [
    closeWithFlagOff,
    handleApply,
    modalConfig,
    openModal,
    updateActivatedTool,
  ]);

  return openEditModel;
}
